import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";

var _BRAND_VS_PART_NUMBER;

import _isNil from 'lodash/isNil';
import _partialRight from 'lodash/partialRight';
import _trim from 'lodash/trim';
import _reduce from 'lodash/reduce';
import _map from 'lodash/map';
import _size from 'lodash/size';
import _join from 'lodash/join';
import _flow from 'lodash/flow';
import _compact from 'lodash/compact';
import _isEmpty from 'lodash/isEmpty';
import _pick from 'lodash/pick';
import _chunk from 'lodash/chunk';
import _get from 'lodash/get';
import { BRAND } from '../../constants/oem.constants';
import { EMPTY_OBJECT, NO_DATA, EMPTY_STRING } from '../../app.constants';
import TEnvReader from '../../readers/Env';
import partReader from '../../readers/Part';
import partKitReader from '../../readers/PartKit';

var getDefaultValue = function getDefaultValue(defaultValue) {
  if (_isNil(defaultValue)) {
    return NO_DATA;
  }

  return defaultValue || defaultValue === '' ? defaultValue : NO_DATA;
};
/**
 * Formats the Part Number based on chunkSize and separator.
 * @param {String} partNumber PartNumber to separate.
 * @param {Number} chunkSize Size to chunk the string by.
 * @param {String} separator Separator to separate the string.
 * @return {String} Returns the joined string separated by separator by chunkSize provided.
 */


var _partNumberFormatter = function _partNumberFormatter(partNumber) {
  var chunkSize = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 5;
  var separator = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '-';
  return _flow([_chunk, _partialRight(_map, function (_char) {
    return _join(_char, '');
  }), _partialRight(_join, separator)])(partNumber, chunkSize);
};

var chunkStringBy = function chunkStringBy(string, chunkSizes) {
  var indexPassed = 0;
  return _reduce(chunkSizes, function (acc, currentChunkSize) {
    if (indexPassed >= _size(string)) return acc;
    var newChunk = string.slice(indexPassed, indexPassed + currentChunkSize); // eslint-disable-next-line operator-assignment

    indexPassed = indexPassed + currentChunkSize;
    return [].concat(_toConsumableArray(acc), [newChunk]);
  }, []);
};

var _volkswagenPartNumberFormatter = _flow([_partialRight(chunkStringBy, [3, 3, 3, 2, 3]), _partialRight(_map, _trim), _partialRight(_join, '-')]);

var volkswagenPartNumberFormatter = function volkswagenPartNumberFormatter(part) {
  var partNumber = partReader.partNumber(part);

  if (_size(partNumber) <= 14) {
    return _volkswagenPartNumberFormatter(partNumber);
  }

  return partNumber;
};

var TOYOTA_PART_NUMBER_CHUNK_SIZE = 5;

var toyotaPartNumberFormatter = function toyotaPartNumberFormatter(part) {
  var partNumber = partReader.partNumber(part);
  return _partNumberFormatter(partNumber, TOYOTA_PART_NUMBER_CHUNK_SIZE, '-');
};

var gmPartNumberFormatter = function gmPartNumberFormatter(part) {
  var partNumber = partReader.partNumber(part); // Currently this requirement is only for part service. The formatting should be done only in parts app

  if (TEnvReader.microServiceApp(process.env) !== 'parts') return partNumber;
  var inventoryPartDetail = partReader.inventoryPartDetail(part); // in inventory object the key is lifeTimeWarranty with cap T where in in master its with small t

  var isLifeTimeWarranty = partReader.lifetimeWarranty(part) || partReader.inventoryLifeTimeWarranty(inventoryPartDetail);
  var isServiceLane = partReader.serviceLaneIndicator(part) || partReader.serviceLaneIndicator(inventoryPartDetail);
  var lifeTimeWarrantyIndicator = !_isNil(isLifeTimeWarranty) && isLifeTimeWarranty ? __('W') : undefined;
  var serviceLaneIndicator = !_isNil(isServiceLane) && isServiceLane ? __('S') : undefined;

  var partIndicators = _compact([serviceLaneIndicator, lifeTimeWarrantyIndicator]);

  return _isEmpty(partIndicators) ? partNumber : "".concat(partNumber, " (").concat(_join(partIndicators, ', '), ")");
};

var NISSAN_PART_NUMBER_CHUNK_SIZE = 5;

var nissanPartNumberFormatter = function nissanPartNumberFormatter(part) {
  var partNumber = partReader.partNumber(part);
  return _partNumberFormatter(partNumber, NISSAN_PART_NUMBER_CHUNK_SIZE, '-');
};

var BRAND_VS_PART_NUMBER_FORMATTER = (_BRAND_VS_PART_NUMBER = {}, _defineProperty(_BRAND_VS_PART_NUMBER, BRAND.VOLKSWAGEN, volkswagenPartNumberFormatter), _defineProperty(_BRAND_VS_PART_NUMBER, BRAND.TOYOTA_TMNA, toyotaPartNumberFormatter), _defineProperty(_BRAND_VS_PART_NUMBER, BRAND.LEXUS, toyotaPartNumberFormatter), _defineProperty(_BRAND_VS_PART_NUMBER, BRAND.GM, gmPartNumberFormatter), _defineProperty(_BRAND_VS_PART_NUMBER, BRAND.NISSAN_NNA, nissanPartNumberFormatter), _defineProperty(_BRAND_VS_PART_NUMBER, BRAND.NISSAN_INFINITI, nissanPartNumberFormatter), _defineProperty(_BRAND_VS_PART_NUMBER, BRAND.NISSAN_NCI, nissanPartNumberFormatter), _BRAND_VS_PART_NUMBER);

var defaultPartNumberFormatter = function defaultPartNumberFormatter(part) {
  return partReader.partNumber(part);
};

var gmCanadaPartNumberFormatter = function gmCanadaPartNumberFormatter(part) {
  var partNumber = partReader.partNumber(part); // Currently this requirement is only for part service. The formatting should be done only in parts app

  if (TEnvReader.microServiceApp(process.env) !== 'parts') return partNumber;
  var inventoryPartDetail = partReader.inventoryPartDetail(part); // in inventory object the key is lifeTimeWarranty with cap T where in in master its with small t

  var isLifeTimeWarranty = partReader.lifetimeWarranty(part) || partReader.inventoryLifeTimeWarranty(inventoryPartDetail);
  var isServiceLane = partReader.serviceLaneIndicator(part) || partReader.serviceLaneIndicator(inventoryPartDetail);
  var isCppp = partReader.cppp(part) || partReader.cppp(inventoryPartDetail);
  var lifeTimeWarrantyIndicator = !_isNil(isLifeTimeWarranty) && isLifeTimeWarranty ? __('W') : undefined;
  var serviceLaneIndicator = !_isNil(isServiceLane) && isServiceLane ? __('S') : undefined;
  var cpppIndicator = isCppp ? __('P') : undefined;

  var partIndicators = _compact([serviceLaneIndicator, lifeTimeWarrantyIndicator, cpppIndicator]);

  return _isEmpty(partIndicators) ? partNumber : "".concat(partNumber, " (").concat(_join(partIndicators, ', '), ")");
};

var BRAND_VS_PART_NUMBER_FORMATTER_FOR_CANADA = _defineProperty({}, BRAND.GM, gmCanadaPartNumberFormatter);

export var formatPartNumberByBrandForCanada = function formatPartNumberByBrandForCanada(part) {
  var partNumber = partReader.partNumber(part);
  if (!partNumber) return null;
  var brandCode = partReader.brandCode(part);
  var partNumberFormatter = BRAND_VS_PART_NUMBER_FORMATTER_FOR_CANADA[brandCode] || defaultPartNumberFormatter;
  return partNumberFormatter(part);
};
export var formatPartNumberByBrand = function formatPartNumberByBrand(part) {
  var partNumber = partReader.partNumber(part);
  if (!partNumber) return null;
  var brandCode = partReader.brandCode(part);
  var partNumberFormatter = BRAND_VS_PART_NUMBER_FORMATTER[brandCode] || defaultPartNumberFormatter;
  return partNumberFormatter(part);
};
export function getPartName(data, languageId) {
  var localeToUse = languageId !== null && languageId !== void 0 ? languageId : TEnvReader.getCurrentLanguageId();
  var partName = partReader.partName(_get(data, ['languages', 'locale', localeToUse])) || partReader.partName(data);
  var description = partReader.partDescription(data);
  return partName || description;
} // partName is a multilang field, we are not changing part description. If part name is not present, we fallback to partDescription if available ( will be in english) but if user overrides it then it will be saved under partName

export var formatPartName = function formatPartName() {
  var data = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;

  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : EMPTY_OBJECT,
      defaultValue = _ref.defaultValue,
      languageId = _ref.languageId;

  var partName = getPartName(data, languageId);
  var formattedPartNumber = formatPartNumberByBrand(data);

  if (formattedPartNumber && partName) {
    return "".concat(formattedPartNumber, " - ").concat(partName);
  }

  var defaultValueToReturn = getDefaultValue(defaultValue);
  return formattedPartNumber || defaultValueToReturn;
};
export var getUserName = function getUserName(user) {
  var _pick2 = _pick(user, ['fname', 'lname', 'displayName']),
      fname = _pick2.fname,
      lname = _pick2.lname,
      displayName = _pick2.displayName;

  if (displayName) return displayName;
  if (_isNil(fname) && _isNil(lname)) return EMPTY_STRING;
  return "".concat(fname, " ").concat(lname);
};
export var formatPartKitName = function formatPartKitName() {
  var data = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;

  var _ref2 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : EMPTY_OBJECT,
      defaultValue = _ref2.defaultValue,
      languageId = _ref2.languageId;

  var localeToUse = languageId !== null && languageId !== void 0 ? languageId : TEnvReader.getCurrentLanguageId();
  var partKitNumber = partKitReader.partKitNumber(data);
  var description = partKitReader.description(_get(data, ['languages', 'locale', localeToUse])) || partKitReader.description(data);

  if (partKitNumber && description) {
    return "".concat(partKitNumber, " - ").concat(description);
  }

  var defaultValueToReturn = getDefaultValue(defaultValue);
  return partKitNumber || defaultValueToReturn;
};